import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { BRAND, IMAGES } from "@/brand";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, ease: EASE, delay }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const jarY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
    const pepperY = useTransform(scrollYProgress, [0, 1], ["0%", "-55%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden pt-[72px]" data-testid="hero-section">
            <motion.div className="absolute inset-0" style={{ y: bgY }}>
                <img
                    src={IMAGES.heroBackdrop}
                    alt="Charred BBQ and smoke"
                    className="h-[120%] w-full object-cover"
                />
                <div className="absolute inset-0 bg-obsidian/85" />
                <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-obsidian to-transparent" />
                <div className="absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-bonnet/20 blur-[140px]" />
                <div className="absolute -right-32 top-16 h-[380px] w-[380px] rounded-full bg-nutmeg/10 blur-[120px]" />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 py-24 sm:px-8">
                <div className="grid items-center gap-16 lg:grid-cols-[1.25fr_1fr]">
                    <div>
                        <MaskedLine delay={0.35}>
                            <span className="inline-flex items-center gap-2 rounded-full border border-thyme/40 bg-thyme/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-thymeglow" data-testid="hero-launch-badge">
                                <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-thymeglow" />
                                Launching soon in Ontario
                            </span>
                        </MaskedLine>

                        <h1 className="mt-8 font-display font-extrabold uppercase leading-[0.88] tracking-tight" data-testid="hero-title">
                            <MaskedLine delay={0.5} className="text-[clamp(3.4rem,11vw,9.5rem)] text-parchment">
                                SpiceJam
                            </MaskedLine>
                            <MaskedLine delay={0.65} className="text-[clamp(3.4rem,11vw,9.5rem)]">
                                <span className="text-outline-gold">Kitchen</span>
                            </MaskedLine>
                        </h1>

                        <MaskedLine delay={0.85} className="mt-8">
                            <p className="font-editorial text-xl italic text-sand sm:text-2xl" data-testid="hero-tagline">
                                {BRAND.tagline}
                            </p>
                        </MaskedLine>

                        <MaskedLine delay={1}>
                            <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-sand/70">
                                <MapPin className="h-3.5 w-3.5 text-bonnetglow" />
                                Made small-batch in Ontario, Canada
                            </span>
                        </MaskedLine>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
                            className="mt-12 flex flex-wrap items-center gap-4"
                        >
                            <button
                                onClick={() => scrollToId("launch-list")}
                                className="rounded-full bg-nutmeg px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-obsidian transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-nutmegwarm hover:shadow-[0_16px_40px_-12px_rgba(234,179,8,0.5)]"
                                data-testid="hero-cta-button"
                            >
                                Join the launch list
                            </button>
                            <button
                                onClick={() => scrollToId("products")}
                                className="rounded-full border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-parchment transition-[border-color,color,transform] duration-300 hover:-translate-y-1 hover:border-nutmeg/60 hover:text-nutmeg"
                                data-testid="hero-secondary-cta"
                            >
                                See what's coming
                            </button>
                        </motion.div>
                    </div>

                    <div className="relative hidden h-[520px] lg:block" data-testid="hero-product-visual">
                        <motion.div
                            style={{ y: jarY }}
                            initial={{ opacity: 0, y: 60, rotate: 6 }}
                            animate={{ opacity: 1, y: 0, rotate: 3 }}
                            transition={{ duration: 1.2, ease: EASE, delay: 1 }}
                            className="absolute right-4 top-0 w-72 overflow-hidden rounded-3xl border border-nutmeg/25 shadow-[0_40px_80px_-24px_rgba(0,0,0,0.8)]"
                        >
                            <img src={IMAGES.jerkJar} alt="Signature Jerk Seasoning jar" className="h-96 w-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
                            <span className="absolute bottom-4 left-4 rounded-full bg-bonnet px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-parchment">
                                Coming soon
                            </span>
                        </motion.div>
                        <motion.div
                            style={{ y: pepperY }}
                            initial={{ opacity: 0, y: 80, rotate: -8 }}
                            animate={{ opacity: 1, y: 0, rotate: -6 }}
                            transition={{ duration: 1.2, ease: EASE, delay: 1.25 }}
                            className="absolute bottom-0 left-0 w-52 overflow-hidden rounded-3xl border border-white/15 shadow-[0_32px_64px_-20px_rgba(0,0,0,0.8)]"
                        >
                            <img src={IMAGES.peppers} alt="Scotch bonnet peppers" className="h-64 w-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent" />
                        </motion.div>
                    </div>
                </div>

                <motion.button
                    onClick={() => scrollToId("products")}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.8, duration: 1 }}
                    className="absolute bottom-10 left-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-sand/60 transition-colors duration-300 hover:text-nutmeg sm:left-8"
                    data-testid="hero-scroll-cue"
                >
                    <ArrowDown className="h-4 w-4 animate-bounce" />
                    Scroll
                </motion.button>
            </motion.div>
        </section>
    );
};

export default Hero;
